import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, Check, Clapperboard, FileText, Image as ImageIcon, Loader2, MapPin, Package, User, Video, X } from "lucide-react";
import { useTheme } from "../../../context/ThemeContext";
import { authenticatedFetch } from "../../../utils/api";
import { API_HOST } from "../../../utils/constants";
import { useDelayedUnmount } from "../../../hooks/useDelayedUnmount";
import { getPublicVideoLabel, getVideoProviderLabel, isSeedanceVideoProvider } from "../../../utils/projectRuntime";
import { isWanxVideoModel } from "../../../utils/wanxVideoCapabilities";

const textOf = (...values) => values.find((value) => typeof value === "string" && value.trim())?.trim() || "";

const getRowVideoProvider = (row = {}) => {
  const provider = textOf(row.video_provider, row.videoProvider, row.video_model, row.videoModel) || "vidu";
  return isWanxVideoModel(provider) ? "wanx" : provider;
};

const truncate = (value, max = 1200) => {
  const text = value || "";
  return text.length > max ? `${text.slice(0, max)}...` : text;
};

const getAssetImageUrl = (asset, variantIndex = -1) => {
  if (!asset) return "";
  if (variantIndex >= 0 && Array.isArray(asset.variations) && asset.variations[variantIndex]?.imageUrl) {
    return asset.variations[variantIndex].imageUrl;
  }
  return asset.imageUrl || "";
};

const buildRefs = (ids = [], options = [], variantIndexes = [], type) => {
  if (!Array.isArray(ids) || !Array.isArray(options)) return [];
  return ids
    .map((id, index) => {
      const asset = options.find((item) => String(item.id) === String(id));
      if (!asset) return null;
      const variantIndex = variantIndexes?.[index] ?? -1;
      return {
        id,
        type,
        name: asset.name || `${type}${index + 1}`,
        imageUrl: getAssetImageUrl(asset, variantIndex),
      };
    })
    .filter(Boolean);
};

const findScriptSlice = (sourceText, row) => {
  const script = sourceText || "";
  if (!script.trim()) return "";
  const anchors = [
    row?.content,
    row?.visual_content,
    row?.action,
  ].map((value) => (value || "").trim()).filter((value) => value.length >= 6);

  const matchedAnchor = anchors.find((anchor) => script.includes(anchor.slice(0, Math.min(anchor.length, 40))));
  if (!matchedAnchor) return truncate(script, 1200);

  const needle = matchedAnchor.slice(0, Math.min(matchedAnchor.length, 40));
  const center = script.indexOf(needle);
  if (center < 0) return truncate(script, 1200);
  const start = Math.max(0, center - 520);
  const end = Math.min(script.length, center + 760);
  return `${start > 0 ? "..." : ""}${script.slice(start, end)}${end < script.length ? "..." : ""}`;
};

const getShotBrief = (row) => {
  if (!row) return "";
  return textOf(row.visual_content, row.action, row.content, row.image_prompt, row.video_prompt);
};

const getVideoModeLabel = (row) => {
  const provider = getRowVideoProvider(row);
  if (isSeedanceVideoProvider(provider)) {
    const providerLabel = getVideoProviderLabel(provider);
    const guaMode = row?.video_gua_mode || "ref2v";
    if (guaMode === "i2v") return `${providerLabel} 首帧生视频`;
    if (guaMode === "t2v") return `${providerLabel} 文生视频`;
    return `${providerLabel} 参考图生视频`;
  }
  if (provider === "wanx") {
    const wanxMode = row?.video_wan_mode || "first_frame";
    const labels = {
      text_to_video: "万相3.0 文生视频",
      first_frame: "万相3.0 首帧生视频",
      first_last_frame: "万相3.0 首尾帧生视频",
      reference_to_video: "万相3.0 参考生视频",
    };
    return labels[wanxMode] || "万相3.0 视频生成";
  }
  if (provider === "vidu" && row?.video_vidu_use_refs) return "Vidu 参考图生视频";
  return "图生视频";
};

const getDirectorSummary = (row) => {
  if (!row?.director_enabled) return "未开启";
  const context = row.director_context || {};
  const transitionLabels = {
    OPENING: "起始镜头",
    SAME_SCENE: "同场连续",
    NEW_SCENE: "新场景",
    TIME_JUMP: "时间跳跃",
    INSERT: "插入镜头",
    REACTION: "反应镜头",
    MONTAGE: "蒙太奇",
  };
  return context.relation_note || transitionLabels[context.transition_type] || "已开启";
};

const RefStrip = ({ title, icon: Icon, items, color }) => (
  <div className="space-y-1.5">
    <div className="flex items-center gap-1.5 text-2xs font-semibold" style={{ color: "var(--color-text-muted)" }}>
      <Icon size={12} style={{ color }} />
      {title}
      <span className="font-mono opacity-70">{items.length}</span>
    </div>
    {items.length > 0 ? (
      <div className="flex gap-2 overflow-x-auto custom-scrollbar pb-1">
        {items.map((item) => (
          <div
            key={`${item.type}-${item.id}`}
            className="w-16 shrink-0 overflow-hidden rounded-md border"
            style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-secondary)" }}
            title={item.name}
          >
            <div className="h-11" style={{ backgroundColor: "var(--color-bg-overlay)" }}>
              {item.imageUrl ? (
                <img src={item.imageUrl} alt={item.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Icon size={14} style={{ color: "var(--color-text-muted)" }} />
                </div>
              )}
            </div>
            <div className="px-1 py-0.5 text-2xs truncate" style={{ color: "var(--color-text-secondary)" }}>
              {item.name}
            </div>
          </div>
        ))}
      </div>
    ) : (
      <div className="text-2xs" style={{ color: "var(--color-text-muted)" }}>未选择</div>
    )}
  </div>
);

const SummaryRow = ({ label, value }) => (
  <div className="flex items-start gap-2 text-2xs">
    <span className="w-16 shrink-0" style={{ color: "var(--color-text-muted)" }}>{label}</span>
    <span className="min-w-0 flex-1 break-words" style={{ color: "var(--color-text-secondary)" }}>{value || "无"}</span>
  </div>
);

const GenerationPreflightPanel = ({
  isOpen,
  type,
  shotIndex,
  rows,
  currentRatio,
  sourceText,
  projectId,
  episodeId,
  characterOptions,
  sceneOptions,
  propOptions,
  multiShotConfig,
  isSubmitting,
  onClose,
  onConfirm,
}) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const [episodeScript, setEpisodeScript] = useState("");
  const [scriptLoadError, setScriptLoadError] = useState("");
  const [isScriptLoading, setIsScriptLoading] = useState(false);

  const { mounted, exiting } = useDelayedUnmount(isOpen, 150);
  // 出场期间 isOpen 已变 false，缓存最后一次有效的 shotIndex 供退场动画渲染
  const lastShotIndexRef = useRef(null);
  if (isOpen && rows?.[shotIndex]) lastShotIndexRef.current = shotIndex;
  const effectiveShotIndex = rows?.[shotIndex] ? shotIndex : lastShotIndexRef.current;

  const row = rows?.[effectiveShotIndex];
  const previousRow = effectiveShotIndex > 0 ? rows?.[effectiveShotIndex - 1] : null;
  const nextRow = effectiveShotIndex < (rows?.length || 0) - 1 ? rows?.[effectiveShotIndex + 1] : null;
  const isVideo = type === "video";
  const title = isVideo ? "视频生成预检" : type === "imageBatch" ? "候选图生成预检" : "图片生成预检";

  const refs = useMemo(() => {
    if (!row) return { characters: [], scenes: [], props: [], all: [] };
    const characters = buildRefs(row.refCharacterIds, characterOptions, row.refCharacterVariantIndexes, "character");
    const scenes = buildRefs(row.refSceneIds, sceneOptions, row.refSceneVariantIndexes, "scene");
    const props = buildRefs(row.refPropIds, propOptions, row.refPropVariantIndexes, "prop");
    const videoRefs = (row.video_gua_ref_images || []).map((url, index) => ({
      id: `${index}-${url}`,
      type: "video-ref",
      name: `视频参考${index + 1}`,
      imageUrl: url,
    }));
    return { characters, scenes, props, videoRefs, all: [...characters, ...scenes, ...props, ...videoRefs] };
  }, [row, characterOptions, sceneOptions, propOptions]);

  const prompt = isVideo
    ? textOf(row?.video_prompt, row?.image_prompt)
    : textOf(row?.image_prompt, row?.visual_content, row?.action);
  const videoModeLabel = getVideoModeLabel(row);
  const effectiveSourceText = episodeScript || sourceText || "";
  const scriptSlice = findScriptSlice(effectiveSourceText, row);

  const loadEpisodeScript = useCallback(async () => {
    if (!projectId) {
      setEpisodeScript("");
      return;
    }
    setIsScriptLoading(true);
    setScriptLoadError("");
    setEpisodeScript("");
    try {
      const data = await authenticatedFetch(`${API_HOST}/api/project/episode/list?projectId=${projectId}`);
      const list = Array.isArray(data) ? data : [];
      const matched = list.find((episode) => String(episode.id) === String(episodeId));
      setEpisodeScript(matched?.scriptContent || "");
    } catch (error) {
      setScriptLoadError(error.message || "剧本加载失败");
    } finally {
      setIsScriptLoading(false);
    }
  }, [projectId, episodeId]);

  useEffect(() => {
    if (!isOpen) return;
    loadEpisodeScript();
  }, [isOpen, loadEpisodeScript]);

  const blockingWarnings = [];
  const softWarnings = [];
  const provider = getRowVideoProvider(row);
  const guaMode = row?.video_gua_mode || "ref2v";
  const hasVideoReferenceMedia = Boolean(
    refs.all.length ||
    row?.video_gua_ref_images?.length ||
    row?.video_gua_ref_videos?.length
  );
  if (isVideo && isSeedanceVideoProvider(provider)) {
    const providerLabel = getVideoProviderLabel(provider);
    if (guaMode === "i2v" && !row?.generated_image) blockingWarnings.push(`${providerLabel} 首帧生视频缺少当前分镜图。`);
    if (guaMode === "ref2v" && !hasVideoReferenceMedia) blockingWarnings.push(`${providerLabel} 参考图生视频还没有参考素材。`);
  } else if (isVideo && provider === "wanx") {
    const wanxMode = row?.video_wan_mode || "first_frame";
    const hasWanxReferences = refs.characters.length + refs.scenes.length + refs.props.length > 0
      || (row?.video_wan_media?.length || 0) > 0;
    if (["first_frame", "first_last_frame"].includes(wanxMode) && !row?.generated_image) {
      blockingWarnings.push("万相3.0首帧模式缺少当前分镜图。");
    }
    if (wanxMode === "first_last_frame" && row?.video_end_shot_index == null) {
      blockingWarnings.push("万相3.0首尾帧模式还没有指定尾帧分镜。");
    }
    if (wanxMode === "reference_to_video" && !hasWanxReferences) {
      blockingWarnings.push("万相3.0参考生视频还没有参考素材。");
    }
  } else if (isVideo && provider === "vidu" && row?.video_vidu_use_refs) {
    if (!refs.all.length && !row?.generated_image) blockingWarnings.push("Vidu 参考图生视频需要参考图或当前分镜图。");
  } else if (isVideo && !row?.generated_image) {
    blockingWarnings.push("图生视频缺少当前分镜图。");
  }
  const preflightWanxMode = row?.video_wan_mode || "first_frame";
  const hasWanxMediaOnlyInput = isVideo && provider === "wanx" && (
    (["first_frame", "first_last_frame"].includes(preflightWanxMode) && row?.generated_image)
    || (preflightWanxMode === "reference_to_video" && (
      (row?.video_wan_media?.length || 0) > 0
      || refs.characters.length + refs.scenes.length + refs.props.length > 0
    ))
  );
  if (!prompt && !hasWanxMediaOnlyInput) blockingWarnings.unshift("当前没有可用提示词。");
  if (!isScriptLoading && !scriptSlice) softWarnings.push("没有检测到剧本片段，本次主要依赖分镜文本。");
  if (!isVideo && row?.director_enabled && !row?.director_context && !row?.director_image_prompt) {
    softWarnings.push("导演介入已开启，但还没有生成导演分析。");
  }
  const warnings = [...blockingWarnings, ...softWarnings];
  const canConfirm = blockingWarnings.length === 0 && !isSubmitting;

  if (!mounted || !row) return null;

  return (
    <div className={`fixed inset-0 z-[800] flex items-center justify-center px-4 py-6 ${exiting ? "animate-out fade-out duration-150" : "animate-in fade-in duration-200"}`} style={{ backgroundColor: "var(--overlay-light)" }}>
      <div
        className={`w-full max-w-3xl max-h-[86vh] rounded-lg border shadow-2xl flex flex-col overflow-hidden ${exiting ? "animate-out fade-out zoom-out-95 duration-150" : "animate-in fade-in zoom-in-95 duration-200"}`}
        style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="h-12 px-4 shrink-0 border-b flex items-center justify-between" style={{ borderColor: "var(--color-border-primary)" }}>
          <div className="min-w-0 flex items-center gap-2">
            <span
              className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
              style={{ backgroundColor: `${isVideo ? theme.colors.bluePrimary : theme.colors.accentPrimary}18`, color: isVideo ? theme.colors.bluePrimary : theme.colors.accentPrimary }}
            >
              {isVideo ? <Video size={16} /> : <ImageIcon size={16} />}
            </span>
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate" style={{ color: "var(--color-text-primary)" }}>{title}</div>
              <div className="text-2xs truncate" style={{ color: "var(--color-text-muted)" }}>
                镜头 #{row.shot_number || shotIndex + 1} · 确认后开始生成
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-md flex items-center justify-center transition-colors hover:bg-[var(--color-bg-tertiary)]"
            style={{ color: "var(--color-text-secondary)" }}
            aria-label="关闭预检"
            title="关闭"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-4 space-y-3">
          {warnings.length > 0 && (
            <section className="rounded-md border p-3 space-y-1.5" style={{ borderColor: "var(--color-warning)", backgroundColor: "var(--color-bg-primary)" }}>
              {warnings.map((warning) => (
                <div key={warning} className="flex items-start gap-2 text-2xs leading-5" style={{ color: "var(--color-warning)" }}>
                  <AlertCircle size={13} className="shrink-0 mt-0.5" />
                  {warning}
                </div>
              ))}
            </section>
          )}

          <section className="rounded-md border p-3 space-y-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)" }}>
            <div className="flex items-center gap-2 text-xs font-semibold" style={{ color: "var(--color-text-primary)" }}>
              <Clapperboard size={14} style={{ color: theme.colors.accentPrimary }} />
              生成摘要
            </div>
            <SummaryRow label="景别" value={row.shot} />
            <SummaryRow label="画幅" value={currentRatio} />
            {!isVideo && <SummaryRow label="导演" value={getDirectorSummary(row)} />}
            {isVideo && <SummaryRow label="模式" value={videoModeLabel} />}
            {isVideo && <SummaryRow label="模型" value={getPublicVideoLabel(provider, "vidu")} />}
            {multiShotConfig?.multiShot && <SummaryRow label="多镜头" value={`自定义 ${multiShotConfig.multiPrompt?.length || 0} 段`} />}
          </section>

          <section className="rounded-md border overflow-hidden" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)" }}>
            <div className="h-9 px-3 border-b flex items-center gap-2" style={{ borderColor: "var(--color-border-primary)" }}>
              <FileText size={14} style={{ color: theme.colors.bluePrimary }} />
              <span className="text-xs font-semibold" style={{ color: "var(--color-text-primary)" }}>最终提示词</span>
            </div>
            <pre className="max-h-40 overflow-y-auto custom-scrollbar p-3 text-2xs leading-5 whitespace-pre-wrap break-words font-sans" style={{ color: "var(--color-text-secondary)" }}>
              {prompt || "无"}
            </pre>
          </section>

          <section className="rounded-md border p-3 space-y-3" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)" }}>
            <div className="text-xs font-semibold" style={{ color: "var(--color-text-primary)" }}>参考图</div>
            <RefStrip title="人物" icon={User} items={refs.characters} color={theme.colors.accentPrimary} />
            <RefStrip title="场景" icon={MapPin} items={refs.scenes} color={theme.colors.bluePrimary} />
            <RefStrip title="道具" icon={Package} items={refs.props} color={theme.colors.purplePrimary} />
            {isVideo && isSeedanceVideoProvider(provider) && (
              <RefStrip title="视频参考" icon={ImageIcon} items={refs.videoRefs || []} color={theme.colors.greenPrimary} />
            )}
          </section>

          <section className="rounded-md border p-3 space-y-2" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)" }}>
            <div className="text-xs font-semibold" style={{ color: "var(--color-text-primary)" }}>前后镜上下文</div>
            <SummaryRow label="上一镜" value={truncate(getShotBrief(previousRow), 180)} />
            <SummaryRow label="当前镜" value={truncate(getShotBrief(row), 180)} />
            <SummaryRow label="下一镜" value={truncate(getShotBrief(nextRow), 180)} />
          </section>

          <section className="rounded-md border overflow-hidden" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-primary)" }}>
            <div className="h-9 px-3 border-b flex items-center gap-2" style={{ borderColor: "var(--color-border-primary)" }}>
              <FileText size={14} style={{ color: "var(--color-text-muted)" }} />
              <span className="text-xs font-semibold" style={{ color: "var(--color-text-primary)" }}>剧本片段</span>
            </div>
            <pre className="max-h-44 overflow-y-auto custom-scrollbar p-3 text-2xs leading-5 whitespace-pre-wrap break-words font-sans" style={{ color: "var(--color-text-muted)" }}>
              {isScriptLoading ? "正在加载当前集剧本..." : scriptLoadError || scriptSlice || "无"}
            </pre>
          </section>
        </div>

        <div className="shrink-0 h-14 px-4 border-t flex items-center justify-end gap-2" style={{ borderColor: "var(--color-border-primary)" }}>
          <button
            type="button"
            onClick={onClose}
            className="h-8 px-3 rounded-md text-xs transition-colors hover:bg-[var(--color-bg-tertiary)]"
            style={{ color: "var(--color-text-secondary)", border: "1px solid var(--color-border-primary)" }}
          >
            取消
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!canConfirm}
            className="h-8 px-3 rounded-md text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50"
            style={{ backgroundColor: isVideo ? theme.colors.bluePrimary : theme.colors.accentPrimary, color: "white" }}
          >
            {isSubmitting ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            确认生成
          </button>
        </div>
      </div>
    </div>
  );
};

export default GenerationPreflightPanel;
